/**
 * Templates API functions - TypeScript version
 * Authentication headers are handled automatically by the axios instance
 */

import { AxiosInstance } from "axios"
import {
  TemplateFileListApiResponse,
  TemplateListApiResponse,
  TemplateParseApiResponse,
  TemplateMergeApiResponse,
  SelectedFile,
  TemplateOptions,
  UserInputs,
} from "types/template.types"

/**
 * Lists all available template files.
 *
 * @param axiosInstance - Authenticated axios instance
 * @returns Promise resolving to the response object containing the list of template files
 * @throws Will throw an error if the request fails
 */
export async function listAllTemplateFiles(
  axiosInstance: AxiosInstance
): Promise<TemplateFileListApiResponse> {
  const response = await axiosInstance.get(`/template-file-list`)
  return response
}

/**
 * Lists templates for a selected file.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param selectedFile - The selected file object containing template name and type
 * @returns Promise resolving to the response object containing the list of templates
 * @throws Will throw an error if the request fails
 */
export async function listTemplates(
  axiosInstance: AxiosInstance,
  selectedFile: SelectedFile
): Promise<TemplateListApiResponse> {
  const response = await axiosInstance.get(
    `/template-get?name=${selectedFile.template}&access=${selectedFile.type}`
  )

  return response
}

/**
 * Parses a selected template with provided data.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The identifier of the data to be parsed
 * @param selectedFile - The selected file object containing template name and type
 * @param template - The template data to be used for parsing
 * @param options - Additional options for parsing, including `inMaster`, `qnrEmpty`, and `includeInstructions`
 * @returns Promise resolving to the response object containing parsed template data
 * @throws Will throw an error if the parsing fails
 */
export async function parseTemplate(
  axiosInstance: AxiosInstance,
  id: string,
  selectedFile: SelectedFile,
  template: string,
  options: TemplateOptions
): Promise<TemplateParseApiResponse> {
  const response = await axiosInstance.post(
    `/template-parse?name=${selectedFile.template}&access=${selectedFile.type}`,
    {
      id: id,
      template: template,
      "in-master": options.inMaster,
      "qnr-empty": options.qnrEmpty,
      "include-instructions": options.includeInstructions ?? true,
    }
  )
  return response
}

/**
 * Merges user inputs with a selected template.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param userInputs - The user inputs to be merged with the template
 * @param selectedFile - The selected file object containing template name and type
 * @param options - Additional options for merging, including `inMaster`, `qnrEmpty`, and `includeInstructions`
 * @returns Promise resolving to the response object containing merged template content
 * @throws Will throw an error if the merge fails
 */
export async function mergeTemplate(
  axiosInstance: AxiosInstance,
  userInputs: UserInputs,
  selectedFile: SelectedFile,
  options: TemplateOptions
): Promise<TemplateMergeApiResponse> {
  const response = await axiosInstance.post(
    `/template-merge?name=${selectedFile.template}&access=${selectedFile.type}`,
    {
      userInputs: userInputs,
      "in-master": options.inMaster,
      "qnr-empty": options.qnrEmpty,
      "include-instructions": options.includeInstructions ?? true,
    }
  )
  return response
}
